'use client';

import { GoogleMap, Marker } from '@react-google-maps/api';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface ComparablePoint {
  id: string;
  lat: number;
  lng: number;
  address?: string;
}

interface ComparablesMapProps {
  dictionary: any;
  subject_latitude: number;
  subject_longitude: number;
  propertyType: 'apartment' | 'house';
  radiusKm?: number;
  limit?: number;
  // Session caching support
  initialComparables?: ComparablePoint[];
  onComparablesLoaded?: (comps: ComparablePoint[]) => void;
  // Pre-fetch support
  nearbyPreFetchPromise?: Promise<ComparablePoint[]>;
}

const mapContainerStyle = { width: '100%', height: '300px' };

export default function ComparablesMap({
  dictionary,
  subject_latitude,
  subject_longitude,
  propertyType,
  radiusKm,
  limit,
  initialComparables,
  onComparablesLoaded,
  nearbyPreFetchPromise,
}: ComparablesMapProps) {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [comparables, setComparables] = useState<ComparablePoint[]>(initialComparables || []);
  const [isFetching, setIsFetching] = useState(false);

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
    setIsMapReady(true);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
    setIsMapReady(false);
  }, []);

  // Fetch nearby comparables (use pre-fetch if available, otherwise fallback to API call)
  useEffect(() => {
    let cancelled = false;
    const fetchNearby = async () => {
      try {
        setIsFetching(true);
        
        // NEW: Check if we have pre-fetched data first
        if (nearbyPreFetchPromise) {
          try {
            const preFetchedData = await nearbyPreFetchPromise;
            if (!cancelled && Array.isArray(preFetchedData) && preFetchedData.length > 0) {
              setComparables(preFetchedData);
              try { onComparablesLoaded?.(preFetchedData); } catch (_) {}
              return; // Use pre-fetched data, skip API call
            }
          } catch (error) {
            console.warn('Pre-fetched data failed, falling back to API call');
          }
        }
        
        // FALLBACK: Original API call logic
        const resp = await fetch('/api/avm/nearby', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lat: subject_latitude,
            lng: subject_longitude,
            propertyType: propertyType === 'house' ? 'HOUSE' : 'APARTMENT',
            // rely on API defaults unless explicitly provided
            ...(typeof radiusKm === 'number' ? { radiusKm } : {}),
            ...(typeof limit === 'number' ? { limit } : {}),
          }),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled && Array.isArray(data.properties)) {
          setComparables(data.properties);
          try { onComparablesLoaded?.(data.properties); } catch (_) {}
        }
      } catch (_) {}
      finally { if (!cancelled) setIsFetching(false); }
    };
    if (!initialComparables || initialComparables.length === 0) {
      fetchNearby();
    }
    return () => { cancelled = true; };
  }, [subject_latitude, subject_longitude, propertyType, radiusKm, limit, initialComparables, onComparablesLoaded, nearbyPreFetchPromise]);

  // Fit bounds when ready and we have comparables (mirror MapTimeframeForm behavior)
  useEffect(() => {
    if (map && isMapReady && comparables.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      comparables.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      if (subject_latitude && subject_longitude) {
        bounds.extend({ lat: subject_latitude, lng: subject_longitude });
      }
      map.fitBounds(bounds, 10);
    }
  }, [map, isMapReady, comparables, subject_latitude, subject_longitude]);

  const mapOptions = useMemo<google.maps.MapOptions>(() => ({
    mapId: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
  }), []);

  // If no subject coordinates, show a neutral placeholder (no default center)
  const hasCoords = typeof subject_latitude === 'number' && typeof subject_longitude === 'number';

  return (
    <div className="relative rounded-lg overflow-hidden border border-zinc-200">
      {!hasCoords ? (
        <div className="h-[300px] w-full bg-zinc-100" />
      ) : (
        <div className="relative google-map-interaction-area">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={15}
            center={{ lat: subject_latitude, lng: subject_longitude }}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={mapOptions}
          >
            {subject_latitude && subject_longitude && (
              <Marker
                position={{ lat: subject_latitude, lng: subject_longitude }}
                title={dictionary?.sellingForm?.subjectPropertyMarkerTitle || 'Your Property'}
                icon={{
                  url: process.env.NEXT_PUBLIC_HOME_MARKER_URL || 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                  scaledSize: new google.maps.Size(22, 22),
                }}
                zIndex={10}
              />
            )}
            {comparables.map((p) => (
              <Marker
                key={p.id}
                position={{ lat: p.lat, lng: p.lng }}
                title={p.address || undefined}
                icon={{
                  url: process.env.NEXT_PUBLIC_COMPARABLES_MARKER_URL || 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                  scaledSize: new google.maps.Size(18, 18),
                }}
              />
            ))}
          </GoogleMap>

          {/* Spinner + blur overlay while fetching */}
          {isFetching && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-sm">
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}


